import React from "react";
import SocketClientProvider from "../contexts/SocketClientProvider";
import { ThemesProvider } from "../contexts/ThemesProvider";
import { SnackbarProvider } from "../contexts/SnackbarProvider";
import { DialogProvider } from "../contexts/DialogProvider";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import "./App.css";

import Root from "../routes/root";
import ErrorPage from "../ErrorPage";
import ProtectedRoute from "../routes/ProtectedRoute";
import Login from "../routes/login";
import Logout from "../routes/logout";
import Profile from "../routes/profile";
import Password from "../routes/password";
import Controls from "../routes/controls";
import Control from "../routes/control";
import ControlDetails from "../routes/controlDetails";
import Devices from "../routes/devices";
import Users from "../routes/users";
import { EditUserPage, NewUserPage } from "../routes/user";
import Automations from "../routes/automations";
import { EditAutomationPage, NewAutomationPage } from "../routes/automation";
import RequestPasswordReset from "../routes/requestPasswordReset";
import ResetPassword from "../routes/resetPassword";
import ApiProvider from "../contexts/ApiProvider";
import UserProvider from "../contexts/UserProvider";
import PublicRoute from "../routes/PublicRoute";
import ControlTimers from "../routes/controlTimers";
import { EditTimerPage, NewTimerPage } from "../routes/timer";
import Scenes from "../routes/scenes";
import { NewScenePage, EditScenePage } from "../routes/scene";
import { NewDevicePage, EditDevicePage } from "../routes/device";
import LanguageProvider from "../contexts/LanguageProvider";

const router = createBrowserRouter([
  {
    path: "/",
    element: <Root />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "login",
        element: <PublicRoute outlet={<Login />} />,
      },
      {
        path: "logout",
        element: <Logout />,
      },
      {
        path: "request_password_reset",
        element: <PublicRoute outlet={<RequestPasswordReset />} />,
      },
      {
        path: "reset_password",
        element: <PublicRoute outlet={<ResetPassword />} />,
      },
      {
        path: "profile/:userid",
        element: <ProtectedRoute outlet={<Profile />} adminOnly={false} />,
      },
      {
        path: "password/:userid",
        element: <ProtectedRoute outlet={<Password />} adminOnly={false} />,
      },
      {
        index: true,
        element: <ProtectedRoute outlet={<Controls />} adminOnly={false} />,
      },
      {
        path: "controls",
        element: <ProtectedRoute outlet={<Controls />} adminOnly={false} />,
      },
      {
        path: "controls/:controlid",
        element: <ProtectedRoute outlet={<Control />} adminOnly={true} />,
      },
      {
        path: "controls/:controlid/timers",
        element: <ProtectedRoute outlet={<ControlTimers />} adminOnly={true} />,
      },
      {
        path: "controls/:controlid/timers/-1",
        element: <ProtectedRoute outlet={<NewTimerPage />} adminOnly={true} />,
      },
      {
        path: "controls/:controlid/timers/:timerid",
        element: <ProtectedRoute outlet={<EditTimerPage />} adminOnly={true} />,
      },
      {
        path: "controls/details/:controlid",
        element: (
          <ProtectedRoute outlet={<ControlDetails />} adminOnly={false} />
        ),
      },
      {
        path: "devices",
        element: <ProtectedRoute outlet={<Devices />} adminOnly={true} />,
      },
      {
        path: "devices/-1",
        element: <ProtectedRoute outlet={<NewDevicePage />} adminOnly={true} />,
      },
      {
        path: "devices/:deviceid",
        element: (
          <ProtectedRoute outlet={<EditDevicePage />} adminOnly={true} />
        ),
      },
      {
        path: "users",
        element: <ProtectedRoute outlet={<Users />} adminOnly={true} />,
      },
      {
        path: "users/-1",
        element: <ProtectedRoute outlet={<NewUserPage />} adminOnly={true} />,
      },
      {
        path: "users/:userid",
        element: <ProtectedRoute outlet={<EditUserPage />} adminOnly={true} />,
      },
      {
        path: "automations",
        element: <ProtectedRoute outlet={<Automations />} adminOnly={true} />,
      },
      {
        path: "automations/-1",
        element: (
          <ProtectedRoute outlet={<NewAutomationPage />} adminOnly={true} />
        ),
      },
      {
        path: "automations/:automationid",
        element: (
          <ProtectedRoute outlet={<EditAutomationPage />} adminOnly={true} />
        ),
      },
      {
        path: "scenes",
        element: <ProtectedRoute outlet={<Scenes />} operatorOnly={true} />,
      },
      {
        path: "scenes/-1",
        element: (
          <ProtectedRoute outlet={<NewScenePage />} operatorOnly={true} />
        ),
      },
      {
        path: "scenes/:sceneid",
        element: (
          <ProtectedRoute outlet={<EditScenePage />} operatorOnly={true} />
        ),
      },
    ],
  },
]);

export default function App() {
  return (
    <ApiProvider>
      <LanguageProvider>
        <ThemesProvider>
          <UserProvider>
            <SocketClientProvider>
              <SnackbarProvider>
                <DialogProvider>
                  <RouterProvider router={router} />
                </DialogProvider>
              </SnackbarProvider>
            </SocketClientProvider>
          </UserProvider>
        </ThemesProvider>
      </LanguageProvider>
    </ApiProvider>
  );
}
